import Image from 'next/future/image';
import { Dispatch, SetStateAction, useState } from 'react';
import { RC_DURATION, TagHelpers } from '@lambda/reebelo';
import { CartLine } from '@lambda/commons/apis/shopify/types/storefront';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import {
  AddToCartParams,
  encodeDecodeForAttr,
  generateAddCartDateTimeAttr,
  generateIDAttr,
} from '@/lib/use-cart';
import { ProductWarrantyT } from '@/lib/reebelocare/types';
import USLineItem from './USLineItem';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';
import {
  trackReebeloCareDeselected,
  trackReebeloCareSelected,
} from '@/lib/analytics/trackProduct';

const MultiLineItem = ({
  device,
  rcToBeAdded,
  setRcToBeAdded,
  isCheckingOut,
  productWarranty,
}: {
  device: CartLine;
  rcToBeAdded: (AddToCartParams & { lineItemId: string })[];
  setRcToBeAdded: Dispatch<
    SetStateAction<(AddToCartParams & { lineItemId: string })[]>
  >;
  isCheckingOut: boolean;
  productWarranty: ProductWarrantyT;
}) => {
  const [checked, setChecked] = useState<RC_DURATION | null>(null);

  const {
    sku,
    image: { url },
    product: { vendor, title },
  } = device.merchandise;

  const { category, psku } = {
    ...TagHelpers.extractTags(device.merchandise.product.tags),
    ...device.merchandise,
  };

  const handleToggle = (duration: RC_DURATION) => {
    const trackingProps: ReebeloCareTrackRequest = {
      entry_point: 'Cart Pop-up',
      reebelocare_id: undefined, // This is getting updated below
      device_psku: psku ?? '',
      device_vendor: vendor,
      device_sku: sku,
      duration: undefined, // This is getting updated below
    };

    if (duration === checked) {
      setChecked((prevState) => {
        if (prevState) {
          if (productWarranty[prevState]) {
            const toBeRemoved = rcToBeAdded.findIndex(
              (item) =>
                item.merchandiseId ===
                `gid://shopify/ProductVariant/${
                  (productWarranty[prevState] as WarrantyT).shopifyVariantId
                }`,
            );

            const newRcToBeAdded = [...rcToBeAdded];

            newRcToBeAdded.splice(toBeRemoved, 1);

            // Dynamic Tracking Props ReebeloCare ID & Duration
            trackingProps.reebelocare_id = (
              productWarranty[prevState] as WarrantyT
            ).shopifyVariantId;
            trackingProps.duration = prevState;

            trackReebeloCareDeselected(trackingProps);
            setRcToBeAdded(newRcToBeAdded);
          }
        }

        return null;
      });
    } else {
      setChecked((prevState) => {
        if (productWarranty[duration]) {
          const newRcToBeAdded = [...rcToBeAdded];

          const toBeAdded: AddToCartParams & { lineItemId: string } = {
            merchandiseId: `gid://shopify/ProductVariant/${
              (productWarranty[duration] as WarrantyT).shopifyVariantId
            }`,
            attributes: [
              ...encodeDecodeForAttr.encode({
                sku,
                title,
              }),
              ...generateIDAttr(vendor, sku),
              ...generateAddCartDateTimeAttr(),
              {
                key: '_offer_category',
                value: category ?? '',
              },
              { key: 'Duration', value: duration },
            ],
            quantity: device.quantity,
            lineItemId: device.id,
          };

          if (prevState && productWarranty[prevState]) {
            const toBeRemoved = rcToBeAdded.findIndex(
              (item) =>
                item.merchandiseId ===
                `gid://shopify/ProductVariant/${
                  (productWarranty[prevState] as WarrantyT).shopifyVariantId
                }`,
            );

            newRcToBeAdded.splice(toBeRemoved, 1);
            // Dynamic Tracking Props ReebeloCare ID & Duration
            trackingProps.reebelocare_id = (
              productWarranty[prevState] as WarrantyT
            ).shopifyVariantId;
            trackingProps.duration = prevState;

            trackReebeloCareDeselected(trackingProps);
          }

          const updatedRcToBeAdded = [...newRcToBeAdded, toBeAdded];

          setRcToBeAdded(updatedRcToBeAdded);
          // Tracking Props ReebeloCare ID
          trackingProps.reebelocare_id = (
            productWarranty[duration] as WarrantyT
          ).shopifyVariantId;
          trackingProps.duration = duration;

          trackReebeloCareSelected(trackingProps);
        }

        return duration;
      });
    }
  };

  if (!productWarranty) return null;

  return (
    <div className="flex flex-col items-center gap-4 pt-4 first:pt-0 xs:flex-row md:pt-8">
      <div className="h-[100px] w-[100px]">
        <div className="h-full w-full">
          <Image
            src={url}
            priority
            loading="eager"
            alt={title}
            height={300}
            width={300}
            className="h-full w-full object-contain"
          />
        </div>
      </div>
      <div className="w-full flex-1 space-y-2">
        {Object.keys(productWarranty).map((d) => {
          const duration = d as RC_DURATION;

          if (!productWarranty[duration]) return null;

          return (
            <USLineItem
              key={duration}
              productWarranty={productWarranty}
              checked={checked}
              duration={duration}
              isCheckingOut={isCheckingOut}
              handleToggle={handleToggle}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MultiLineItem;
